/**
 * piiDetector.js — détection PII locale, zéro réseau, zéro coût.
 *
 * Utilisé par `useWebSpeechPII` pour scanner en temps réel la transcription
 * du micro local (Web Speech API). Aligné sur `backend/app/services/pii_redactor.py`.
 *
 * 🔒 Le texte brut ne sort JAMAIS du navigateur — seuls `{type, hash}` sont
 *    envoyés au serveur via POST /pii-event.
 */

const WHITELIST_DOMAINS = ['coachdia.com', 'owen.coachdia.com'];

// Normalisation phonétique FR (sous-ensemble aligné serveur)
const PHONETIC_RULES = [
  [/\s(?:arobase|arobas|at|chez)\s/gi, '@'],
  [/\s(?:point|dot)\s/gi, '.'],
  [/\s(?:tiret|dash|trait d'union)\s/gi, '-'],
  [/\s(?:slash|barre oblique)\s/gi, '/'],
];

const DIGIT_WORDS = {
  'zéro': '0', 'zero': '0', 'un': '1', 'une': '1', 'deux': '2',
  'trois': '3', 'quatre': '4', 'cinq': '5', 'six': '6',
  'sept': '7', 'huit': '8', 'neuf': '9',
};

const DIGIT_WORD_LIST = Object.keys(DIGIT_WORDS).sort((a, b) => b.length - a.length);
const DIGIT_WORD_RE = new RegExp(`\\b(${DIGIT_WORD_LIST.join('|')})\\b`, 'gi');
const DIGIT_SEQUENCE_RE = new RegExp(
  `(\\b(?:${DIGIT_WORD_LIST.join('|')})\\b[\\s-]+){2,}(\\b(?:${DIGIT_WORD_LIST.join('|')})\\b)`,
  'gi'
);

const PATTERNS = {
  email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g,
  phone_fr: /(?:\+33|0033|0)\s?[1-9](?:[\s.\-]?\d{2}){4}\b/g,
  phone_intl: /\+\d{1,3}[\s.\-]?\d(?:[\s.\-]?\d){7,13}\b/g,
  url: /\bhttps?:\/\/[^\s<>"']+|\bwww\.[^\s<>"']+\.[A-Za-z]{2,}\b/gi,
};

const PUBLIC_TYPE = {
  email: 'email',
  phone_fr: 'phone',
  phone_intl: 'phone',
  url: 'url',
};

function normalize(text) {
  let out = ` ${text} `;
  for (const [re, repl] of PHONETIC_RULES) out = out.replace(re, repl);
  // Digits en lettres → chiffres (uniquement séquences de 2+ mots-chiffres)
  out = out.replace(DIGIT_SEQUENCE_RE, (seq) =>
    seq.replace(DIGIT_WORD_RE, (m) => DIGIT_WORDS[m.toLowerCase()] || m)
  );
  return out;
}

function isWhitelisted(match, type) {
  const low = match.toLowerCase();
  if (type === 'email') {
    const domain = low.split('@').pop();
    return WHITELIST_DOMAINS.includes(domain);
  }
  if (type === 'url') {
    return WHITELIST_DOMAINS.some((d) => low.includes(d));
  }
  return false;
}

/**
 * Hash SHA-256 tronqué 8 chars via Web Crypto API (natif navigateur).
 * Non réversible en pratique, utilisé uniquement pour dedup côté serveur.
 */
async function hashShort(s) {
  const encoder = new TextEncoder();
  const data = encoder.encode(s);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const bytes = new Uint8Array(digest);
  return Array.from(bytes.slice(0, 4))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Détecte les PII dans un texte.
 * @returns Promise<Array<{type: 'email'|'phone'|'url', hash: string}>>
 */
export async function detectPII(text) {
  if (!text || typeof text !== 'string') return [];
  const normalized = normalize(text);
  const seen = new Set();
  const out = [];

  for (const [key, pattern] of Object.entries(PATTERNS)) {
    pattern.lastIndex = 0;
    let m;
    while ((m = pattern.exec(normalized)) !== null) {
      const raw = m[0];
      if (isWhitelisted(raw, PUBLIC_TYPE[key])) continue;
      const hash = await hashShort(raw);
      const dedupKey = `${PUBLIC_TYPE[key]}:${hash}`;
      if (seen.has(dedupKey)) continue;
      seen.add(dedupKey);
      out.push({ type: PUBLIC_TYPE[key], hash });
    }
  }
  return out;
}

/**
 * Teste si le navigateur supporte la Web Speech API.
 */
export function isSpeechRecognitionSupported() {
  return typeof window !== 'undefined' &&
    (typeof window.SpeechRecognition !== 'undefined' ||
     typeof window.webkitSpeechRecognition !== 'undefined');
}
